import { Translate } from '@components'
import { FlipSwitch } from '@components-deprecated'

import { Button, Text } from '@billy/design-system'
import { yupResolver } from '@hookform/resolvers/yup'
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useFetchCompany } from '@modules-deprecated/app/companies/hooks/useFetchCompany'
import { Company } from '@modules-deprecated/app/companies/types'
import { getCompanyName } from '@modules-deprecated/app/companies/utils/getCompanyName'
import { Country } from '@modules-deprecated/app/countries/types'
import { Currency } from '@modules-deprecated/app/currencies/types'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useUserUmbrellas } from '@modules-deprecated/app/umbrellas'

import { NAME_REGEX } from '../../../../constants/nameRegex'
import { useLocale } from '../../../../hooks/useLocale'
import { useOrganizationUiSetup } from '../../../../hooks/useOrganizationUiSetup'
import { getAppBuildTheme } from '../../../../utils/getAppBuildTheme'
import { getClassName } from '../../../../utils/getClassName'
import { useSignupGuide } from '../../context/signupGuideContext'
import { useInternalUmbrellaId } from '../../hooks/useInternalUmbrellaId'
import { useTrackEventOnLoad } from '../../hooks/useTrackEventOnLoad'
import { ErrorMessage } from '../ErrorMessage'
import { GuideFormWrapper } from '../GuideFormWrapper'
import { GuideHeader } from '../GuideHeader'
import { GuideLayout } from '../GuideLayout'
import { OrganizationCVRData } from './elements/OrganizationCVRData'
import { OrganizationManualData } from './elements/OrganizationManualData'
import { SubscriptionSection } from './elements/SubscriptionSection'
import { useHandleCreateOrganizationSubmit } from './hooks/useHandleCreateOrganizationSubmit'
import * as Styled from './styles'
import { CreateOrganizationForm, defaultValues, getValidationSchema } from './utils/formData'

const DEFAULT_COUNTRY: Partial<Country> = { id: 'DK' }
const DEFAULT_CURRENCY: Partial<Currency> = { id: 'DKK' }

const theme = getAppBuildTheme()

export const CreateOrganization = (): ReactElement => {
  const { t } = useTranslation()
  const { isInternalFlow, isInternalUmbrellaFlow, isUserLoading, user } = useSignupGuide()
  const { userUmbrellas } = useUserUmbrellas()
  const umbrellaId = useInternalUmbrellaId()
  const companySelectTextRef = useRef('')
  const { code: language } = useLocale()
  const { isUiSetupShineInvoicingAccounting } = useOrganizationUiSetup()
  const { organization, isLoading: isOrganizationLoading } = useUserOrganization()

  // NL case - on first lookup we use very limited list of companies, missing e.g. zipcode and street number - we need to refetch full details
  // https://linear.app/ageras-new/issue/BOOK-1574/extend-company-registration-number-lookup-to-netherlands
  const [selectedCompany, setSelectedCompany] = useState<Company | undefined>(undefined)
  const { company: companyWithRefetchedDetails } = useFetchCompany(selectedCompany?.id || '', language)

  const validationSchema = useMemo(() => getValidationSchema(t), [t])

  const form = useForm<CreateOrganizationForm>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  const { setValue, reset, getValues } = form

  const userName = user?.name
  const userNameForm = form.watch('userName')
  // An existing name that fails the format check must stay editable, otherwise the user can't fix it and submit is blocked
  const isExistingUserNameValid = !!userName && NAME_REGEX.test(userName.trim())
  const isUserNameInputHidden =
    (isInternalFlow || isInternalUmbrellaFlow) && !isUserLoading && isExistingUserNameValid && !!userNameForm

  const umbrellaName = useMemo(() => {
    if (!isInternalUmbrellaFlow) {
      return
    }

    return userUmbrellas.find((umbrella) => umbrella.umbrellaId === umbrellaId)?.name
  }, [isInternalUmbrellaFlow, umbrellaId, userUmbrellas])

  // Mutations

  const {
    submit,
    errorCode: submitErrorCode,
    isLoading: isSubmitting,
    isError: isSubmitError,
  } = useHandleCreateOrganizationSubmit()

  // Computed values

  const currentFormValue = form.watch()
  const isManualMode = currentFormValue.isManualMode

  // Lifecycle

  useTrackEventOnLoad('xxx - iam org create viewed', {
    brand: theme,
  })

  useEffect(() => {
    if (!isUiSetupShineInvoicingAccounting) {
      return
    }

    const formValues = getValues()
    reset({
      ...defaultValues,
      userName: formValues.userName || userName || '',
      paidBy: formValues.paidBy,
      isManualMode: true,
      orgCountry: organization?.countryId ? { id: organization.countryId } : DEFAULT_COUNTRY,
      orgCurrency: organization?.baseCurrencyId ? { id: organization.baseCurrencyId } : DEFAULT_CURRENCY,
    })
    // isUiSetupShineInvoicingAccounting is only true once org is loaded, so organization is stable at this point
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUiSetupShineInvoicingAccounting])

  useEffect(() => {
    if (userName) {
      setValue('userName', userName)
    }
  }, [setValue, userName])

  useEffect(() => {
    if (isInternalUmbrellaFlow) {
      setValue('paidBy', 'admin')
    }
  }, [setValue, isInternalUmbrellaFlow])

  // Functions

  const clearOrganizationData = useCallback(
    (isManualMode: boolean) => {
      if (!isManualMode) {
        companySelectTextRef.current = ''
      }
      const formValues = getValues()
      const prevUserName = formValues.userName || userName
      const prevPaidBy = formValues.paidBy
      reset({
        ...defaultValues,
        orgName: companySelectTextRef.current,
        userName: prevUserName,
        isManualMode,
        paidBy: prevPaidBy,
        ...(isManualMode ? { orgCountry: DEFAULT_COUNTRY, orgCurrency: DEFAULT_CURRENCY } : {}),
      })
    },
    [reset, getValues, userName],
  )

  const handleManualModeSwitcherClick = useCallback(() => {
    clearOrganizationData(!isManualMode)
  }, [isManualMode, clearOrganizationData])

  const setFiscalYearData = useCallback(
    (company: Company) => {
      if (company.fiscalYearEnd) {
        const fiscalYearEndMonth = company.fiscalYearEnd.substring(0, 2)
        setValue('orgFiscalYearEndMonth', fiscalYearEndMonth)
      }

      if (company.firstFiscalYearStart) {
        setValue('orgFirstFiscalYearStart', company.firstFiscalYearStart)
      }

      if (company.firstFiscalYearEnd) {
        setValue('orgFirstFiscalYearEnd', company.firstFiscalYearEnd)
      }
    },
    [setValue],
  )

  const handleCompanySelectTextChange = useCallback((value: string) => {
    companySelectTextRef.current = value
  }, [])

  const updateForm = useCallback(
    (company: Company) => {
      setValue('orgName', getCompanyName(company))
      setValue('orgStreet', company.street + ' ' + company.number)
      setValue('orgZipcode', company.zipcode)
      setValue('orgCity', company.city)
      setValue('orgCountry', DEFAULT_COUNTRY)
      setValue('orgCurrency', DEFAULT_CURRENCY)
      setFiscalYearData(company)
    },
    [setValue, setFiscalYearData],
  )

  const handleCompanySelect = useCallback(
    (company: Company) => {
      if (!company) {
        companySelectTextRef.current = ''
        reset(defaultValues)
        return
      }

      // Case for NL kvk lookup - incomplete data on first lookup
      if (!company.zipcode && !company.number && language === 'nl') {
        setSelectedCompany(company)
        return
      }

      companySelectTextRef.current = getCompanyName(company) ?? ''
      updateForm(company)
    },
    [updateForm, reset, language],
  )

  const handleFormSubmit = useCallback(
    (values: CreateOrganizationForm) => {
      submit(values)
    },
    [submit],
  )

  const getGuideHeaderTitle = useCallback(() => {
    if (isInternalFlow) {
      return t('signup_guide.create_internal_org.title')
    }

    if (isInternalUmbrellaFlow) {
      return t('signup_guide.create_internal_umbrella_org.title')
    }

    return t('signup_guide.create_org.title')
  }, [isInternalFlow, isInternalUmbrellaFlow, t])

  const getGuideHeaderDescription = useCallback(() => {
    if (isInternalFlow) {
      return t('signup_guide.create_internal_org.description')
    }

    if (isInternalUmbrellaFlow) {
      return t('signup_guide.create_internal_umbrella_org.description', { name: umbrellaName })
    }

    return t('signup_guide.create_org.description')
  }, [isInternalFlow, isInternalUmbrellaFlow, t, umbrellaName])

  // Case for NL kvk lookup - incomplete data on first lookup - we need to wait until we have refetched full details
  useEffect(() => {
    if (companyWithRefetchedDetails && Object.keys(companyWithRefetchedDetails).length > 0) {
      updateForm(companyWithRefetchedDetails)
    }
  }, [companyWithRefetchedDetails, updateForm])

  return (
    <GuideLayout autoFocus>
      <FormProvider {...form}>
        <GuideHeader title={getGuideHeaderTitle()} description={getGuideHeaderDescription()} />

        <GuideFormWrapper onSubmit={form.handleSubmit(handleFormSubmit)}>
          {(!!submitErrorCode || isSubmitError) && (
            <ErrorMessage errorCode={submitErrorCode} defaultMessage={t('signup_guide.create_org.error_create_org')} />
          )}

          <Styled.Input
            formControl={form.control}
            name="userName"
            label={t('signup_guide.create_org.form.name.label')}
            placeholder={t('signup_guide.create_org.form.name.placeholner')}
            disabled={isSubmitting}
            hidden={isUserNameInputHidden}
            inputSize="lg"
          />
          {!isOrganizationLoading && !isUiSetupShineInvoicingAccounting && (
            <>
              <Styled.CompanySelector
                formControl={form.control}
                onTextChange={handleCompanySelectTextChange}
                onItemSelect={handleCompanySelect}
                name="orgCompany"
                label={t('signup_guide.create_org.form.cvr.label')}
                placeholder={t('signup_guide.create_org.form.cvr.placeholder')}
                disabled={isManualMode || isSubmitting}
                inputSize="lg"
                listWidth="100%"
                withSelectEvent
              />
              <Styled.DetailsSwitcherWrapper>
                <Styled.SwitcherTitle className={getClassName('label-deprecated')}>
                  <Text colorVariant="secondary">
                    <Translate value="signup_guide.create_org.form.manual_switcher" />
                  </Text>
                </Styled.SwitcherTitle>

                <FlipSwitch
                  formControl={form.control}
                  name="isManualMode"
                  onClick={handleManualModeSwitcherClick}
                  checked={isManualMode}
                />
              </Styled.DetailsSwitcherWrapper>
            </>
          )}

          <OrganizationCVRData formControl={form.control} visible={!isManualMode && !!currentFormValue.orgCompany} />
          <OrganizationManualData formControl={form.control} visible={isManualMode} disabled={isSubmitting} />

          {isInternalUmbrellaFlow && !!umbrellaId && (
            <SubscriptionSection formControl={form.control} disabled={isSubmitting} />
          )}

          <Styled.ButtonWrapper>
            <Button type="submit" size="lg" loading={isSubmitting} fullWidth>
              {isInternalFlow || isInternalUmbrellaFlow
                ? t('signup_guide.create_internal_org.form.submit.label')
                : t('signup_guide.create_org.form.submit.label')}
            </Button>
          </Styled.ButtonWrapper>
        </GuideFormWrapper>
      </FormProvider>
    </GuideLayout>
  )
}
